import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { pdApi, type PdFramework } from '../api';
import './FrameworkBuilder.css';
import { HelpPanel } from '../HelpPanel';
import { FRAMEWORK_BUILDER } from '../helpContent';

/**
 * Settings → Assessment Framework → Framework Builder.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/framework-builder.ejs` —
 * "single-screen table layout (Dimension · Component · Question · Weight + expandable
 * Q&A)", in the source's own words.
 *
 * THE WHOLE FRAMEWORK IS ONE TABLE. Every row is a dimension → component → question with
 * its weight, and each level's cell is drawn only on its FIRST row, so a dimension with
 * three components spans three rows without repeating itself. That is what makes a
 * 40-question framework readable in one screen instead of forty cards.
 *
 * WEIGHT IS FIVE DOTS, not a dropdown — click the fourth dot to set weight 4. The colour
 * runs red → green across the scale, so the shape of a framework's weighting is visible
 * down the column without reading a single number.
 *
 * A LOCKED FRAMEWORK IS READ-ONLY: once anyone has scored against it, changing the
 * questions would invalidate the answers already given.
 */

/**
 * The three aspects an Inventory Registry dimension may be answered under, and who answers
 * each. Mirrors `portfolio_definition/aspects.ts` on the server.
 *
 * Operational and Security are deliberately absent: APR has five aspects, IR has three, and
 * the database refuses the other two outright.
 */
const IR_ASPECTS = [
  { aspect: 'Technical', owner: 'Opportunity Owner' },
  { aspect: 'Business', owner: 'Business Process Owner' },
  { aspect: 'Strategy', owner: 'Executive Owner' },
] as const;

type Dict = Record<string, unknown>;

/** The source's weight palette, `framework-builder.ejs:37`. */
const WEIGHT_COLORS: Record<number, string> = {
  1: '#E53935',
  2: '#FB8C00',
  3: '#FDD835',
  4: '#43A047',
  5: '#2E7D32',
};

export function FrameworkBuilder() {
  const location = useLocation();
  const [frameworks, setFrameworks] = useState<PdFramework[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [framework, setFramework] = useState<PdFramework | null>(null);
  const [tree, setTree] = useState<Dict[]>([]);
  const [scoreColors, setScoreColors] = useState<Record<number, string>>({});
  const [saved, setSaved] = useState(false);

  /*
    THE PAGE HEIGHT IS MEASURED — the same pattern as Delivery Pathway and Process
    Hierarchy, and for the same reason: this shell never bounds its content area, so
    `height: 100%` resolves against nothing and the window scrolls instead of the table.
    Callback ref, because the table mounts after the loading gate.
  */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState('All');
  const [openAnswers, setOpenAnswers] = useState<Set<number>>(new Set());

  // `?framework=…` is how the Frameworks screen's Designer button arrives here.
  const requested = new URLSearchParams(location.search).get('framework');

  useEffect(() => {
    void (async () => {
      try {
        const data = await pdApi.settings.frameworks();
        setFrameworks(data.frameworks);
        const pick =
          (requested && data.frameworks.find((f) => f.framework_id === requested)) ??
          data.frameworks.find((f) => f.is_default) ??
          data.frameworks[0];
        if (pick) setFrameworkId(pick.framework_id);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [requested]);

  const load = useCallback(async () => {
    if (!frameworkId) return;
    setError(null);
    try {
      const data = await pdApi.settings.builder(frameworkId);
      setFramework(data.framework);
      setTree(data.tree);
      setScoreColors(data.scoreColors);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [frameworkId]);

  useEffect(() => {
    void load();
  }, [load]);

  const locked = Boolean(framework?.is_locked);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Commit an inline edit only when the value actually changed. */
  const commit = (current: unknown, next: string, save: (v: string) => Promise<unknown>) => {
    if (String(current ?? '') === next) return;
    void run(() => save(next));
  };

  /* Any further edit clears the confirmation — it describes a moment, not a state. */
  const scoreColor = (v: unknown) => scoreColors[Number(v)] ?? '#9e9e9e';

  const shownTree = useMemo(
    () =>
      catFilter === 'All'
        ? tree
        : tree.filter((d) => String(d.category ?? 'Business') === catFilter),
    [tree, catFilter],
  );

  const totals = useMemo(() => {
    const comps = tree.reduce((s, d) => s + ((d.components as Dict[]) ?? []).length, 0);
    const qs = tree.reduce(
      (s, d) =>
        s +
        ((d.components as Dict[]) ?? []).reduce(
          (a, c) => a + ((c.questions as Dict[]) ?? []).length,
          0,
        ),
      0,
    );
    return { comps, qs };
  }, [tree]);

  const toggleAnswers = (qid: number) => {
    const next = new Set(openAnswers);
    if (next.has(qid)) next.delete(qid);
    else next.add(qid);
    setOpenAnswers(next);
  };

  /** The dimension cell — name, code, category chip and the "+ component" link. */
  const dimCell = (d: Dict) => {
    const category = String(d.category ?? 'Business');
    return (
      <>
        <div className="fb-cell-row">
          <input
            className="fb-edit dim-name"
            defaultValue={String(d.name ?? '')}
            disabled={locked}
            onBlur={(e) =>
              commit(d.name, e.target.value, (v) =>
                pdApi.settings.updateDimension(Number(d.id), { name: v }),
              )
            }
          />
          <button
            type="button"
            className="fb-del"
            title="Remove dimension"
            disabled={locked || busy}
            onClick={() => {
              if (!window.confirm(`Remove "${String(d.name)}" from this framework?`)) return;
              return run(() => pdApi.settings.removeDimension(frameworkId, Number(d.id)));
            }}
          >
            🗑️
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <span className="fb-code">{String(d.dimension_code ?? '')}</span>
          {/* Clicking the chip flips Business ↔ Technology, as the source does. */}
          <button
            type="button"
            className={`cat-chip cat-${category.toLowerCase()}`}
            title="Click to switch Business / Technology"
            disabled={locked || busy}
            onClick={() =>
              run(() =>
                pdApi.settings.updateDimension(Number(d.id), {
                  category: category === 'Business' ? 'Technology' : 'Business',
                }),
              )
            }
          >
            {category}
          </button>

          {/*
            WHO ANSWERS THIS DIMENSION — a different question from the chip beside it.

            `category` GROUPS the dimension (Business / Technology). This decides WHO IS ASKED
            its questions, and the two do not line up: Strategic & Financial Value is grouped
            Business and answered by the Executive Owner. Keeping them as one control would
            have made that dimension unroutable.

            Three options only. Operational and Security exist in APR but cannot be assigned to
            an Inventory Registry dimension — owner ruling, 2026-08-27, enforced by a CHECK
            constraint in migration 0087 so this selector is a convenience rather than the rule.
          */}
          <select
            className="fb-edit"
            style={{ maxWidth: 140, fontSize: 12 }}
            title="Who answers this dimension's questions"
            value={String(d.responsible_aspect ?? 'Technical')}
            disabled={locked || busy}
            onChange={(e) =>
              run(() =>
                pdApi.settings.updateDimension(Number(d.id), {
                  responsible_aspect: e.target.value,
                }),
              )
            }
          >
            {IR_ASPECTS.map((a) => (
              <option key={a.aspect} value={a.aspect}>
                {a.aspect} — {a.owner}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginTop: 4 }}>
          <button
            type="button"
            className="fb-link"
            disabled={locked || busy}
            onClick={() =>
              run(() => pdApi.settings.addComponent(frameworkId, { dimension_id: d.id }))
            }
          >
            + component
          </button>
        </div>
      </>
    );
  };

  const weightDots = (comp: Dict) => {
    const w = Number(comp.weightage ?? 3);
    return (
      <div className={`wt-dots${locked ? ' wt-dots--locked' : ''}`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className="wt-dot"
            title={`Set weight to ${n}`}
            disabled={locked || busy}
            style={n <= w ? { background: WEIGHT_COLORS[w] } : undefined}
            onClick={() =>
              run(() => pdApi.settings.updateComponent(Number(comp.id), { weightage: n }))
            }
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  if (loading) return <div className="fb-wrap fb-empty">Loading…</div>;

  return (
    <div className="fb-wrap" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <div className="fb-head">
        <div>
          {/* The module's heading, not this screen's own — 24px/700, the same as Project
              Registry and Projects Dashboard (owner, 2026-08-09). */}
          <h1 className="pd-page-header__title">Framework Builder</h1>
          <p className="fb-sub">
            <strong>{framework?.framework_name}</strong> &middot; {framework?.framework_id} —{' '}
            {tree.length} dimensions &middot; {totals.comps} components &middot; {totals.qs}{' '}
            questions. Define the whole framework on one screen.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={FRAMEWORK_BUILDER} />
        </div>
        <div className="fb-head-actions">
          <label className="fb-filter-label" htmlFor="fb-fw">
            Framework:
          </label>
          <select
            id="fb-fw"
            className="fb-filter"
            value={frameworkId}
            onChange={(e) => setFrameworkId(e.target.value)}
          >
            {frameworks.map((f) => (
              <option key={f.framework_id} value={f.framework_id}>
                {f.framework_name}
                {f.is_locked ? ' 🔒' : ''}
              </option>
            ))}
          </select>

          <label className="fb-filter-label" htmlFor="dimCatFilter">
            Show:
          </label>
          <select
            id="dimCatFilter"
            className="fb-filter"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
          >
            <option value="All">All dimensions</option>
            <option value="Business">Business</option>
            <option value="Technology">Technology</option>
          </select>

          <button
            type="button"
            className="fb-add-dim"
            id="addDimBtn"
            disabled={locked || busy}
            onClick={() => run(() => pdApi.settings.addDimension(frameworkId))}
          >
            + Add dimension
          </button>

          {/*
            Save sits on THIS row, beside Add dimension — it was up in the header-actions
            stack and read as detached from the screen it acts on (owner, 2026-08-09).

            Every edit here already saves itself: a field on blur, a dot or a badge on click.
            What this button honestly does is commit whatever is still being typed — blurring
            the focused input fires the same commit path — then re-read the tree so what is
            on screen is what is stored. It confirms; it is not the only way to save.
          */}
          <button
            type="button"
            className="fb-save"
            disabled={locked || busy}
            onClick={() => {
              (document.activeElement as HTMLElement | null)?.blur();
              setTimeout(() => void load().then(() => setSaved(true)), 150);
            }}
          >
            Save
          </button>
          {saved ? <span className="fb-saved">All changes saved</span> : null}
        </div>
      </div>

      {error ? <div className="fb-error">{error}</div> : null}
      {locked ? (
        <div className="fb-locked-note">
          This framework is locked because it has been used for scoring. Editing its
          questions would invalidate the answers already recorded, so it is read-only.
        </div>
      ) : null}

      <div className="fb-card">
        <div className="fb-card-content">
          <table className="fb-table">
            <thead>
              <tr>
                {/*
                  REBALANCED so the questions fit. QUESTION carries the longest text on the
                  screen AND its Q&A and delete controls, and at 43% its inputs were cutting
                  words off the end; WEIGHT was holding 205px for five 17px dots. The width
                  goes where the text is.
                */}
                {/*
                  Widths sized to the longest value each column actually holds:
                  "Organizational Readiness & Adoption", "Executive sponsorship & change
                  readiness", and the questions — which have the most room to spare even at
                  46%. WEIGHT needs only its five 17px dots.
                */}
                <th style={{ width: '22%' }}>DIMENSION</th>
                <th style={{ width: '22%' }}>COMPONENT</th>
                <th style={{ width: '46%' }}>QUESTION</th>
                <th style={{ width: '10%', textAlign: 'center' }}>WEIGHT</th>
              </tr>
            </thead>
            <tbody>
              {shownTree.map((dim) => {
                const components = (dim.components as Dict[]) ?? [];

                // A dimension with nothing under it still gets a row, so it can be filled.
                if (components.length === 0) {
                  return (
                    <tr key={`d${String(dim.id)}`} className="dim-first">
                      <td>{dimCell(dim)}</td>
                      <td colSpan={3} style={{ color: '#94a3b8' }}>
                        <button
                          type="button"
                          className="fb-link"
                          disabled={locked || busy}
                          onClick={() =>
                            run(() =>
                              pdApi.settings.addComponent(frameworkId, { dimension_id: dim.id }),
                            )
                          }
                        >
                          + Add component
                        </button>
                      </td>
                    </tr>
                  );
                }

                let dimStart = true;
                const rows: JSX.Element[] = [];

                for (const comp of components) {
                  const questions = (comp.questions as Dict[]) ?? [];
                  // A component with no questions still occupies one row, offering the link.
                  const qlist: (Dict | null)[] = questions.length ? questions : [null];

                  qlist.forEach((q, qi) => {
                    const firstOfComp = qi === 0;
                    const firstOfDim = dimStart;
                    dimStart = false;
                    const answers = q ? ((q.answers as Dict[]) ?? []) : [];

                    rows.push(
                      <tr
                        key={`c${String(comp.id)}-q${q ? String(q.id) : 'none'}`}
                        className={`comp-row${firstOfDim ? ' dim-first' : ''}`}
                      >
                        <td>{firstOfDim ? dimCell(dim) : null}</td>

                        <td>
                          {firstOfComp ? (
                            <>
                              <div className="fb-cell-row">
                                <input
                                  className="fb-edit"
                                  defaultValue={String(comp.name ?? '')}
                                  disabled={locked}
                                  onBlur={(e) =>
                                    commit(comp.name, e.target.value, (v) =>
                                      pdApi.settings.updateComponent(Number(comp.id), { name: v }),
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  className="fb-del"
                                  title="Remove component"
                                  disabled={locked || busy}
                                  onClick={() => {
                                    if (
                                      !window.confirm(
                                        `Remove "${String(comp.name)}" from this framework?`,
                                      )
                                    )
                                      return;
                                    return run(() =>
                                      pdApi.settings.removeComponent(frameworkId, Number(comp.id)),
                                    );
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>
                              <div style={{ marginTop: 3 }}>
                                <button
                                  type="button"
                                  className="fb-link"
                                  disabled={locked || busy}
                                  onClick={() =>
                                    run(() =>
                                      pdApi.settings.addQuestion({ component_id: comp.id }),
                                    )
                                  }
                                >
                                  + question
                                </button>
                              </div>
                            </>
                          ) : null}
                        </td>

                        <td>
                          {q ? (
                            <div className="fb-cell-row">
                              <input
                                className="fb-edit"
                                style={{ color: '#334155' }}
                                defaultValue={String(q.question_text ?? '')}
                                disabled={locked}
                                onBlur={(e) =>
                                  commit(q.question_text, e.target.value, (v) =>
                                    pdApi.settings.updateQuestion(Number(q.id), {
                                      question_text: v,
                                    }),
                                  )
                                }
                              />
                              {answers.length ? (
                                <button
                                  type="button"
                                  className={`qa-toggle${openAnswers.has(Number(q.id)) ? ' open' : ''}`}
                                  onClick={() => toggleAnswers(Number(q.id))}
                                >
                                  Q&amp;A <span className="qa-caret">▾</span>
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="fb-del"
                                title="Remove question"
                                disabled={locked || busy}
                                onClick={() => {
                                  if (
                                    !window.confirm(
                                      'Delete this question? It is removed from every framework that uses this component.',
                                    )
                                  )
                                    return;
                                  return run(() =>
                                    pdApi.settings.deleteQuestion(Number(q.id)),
                                  );
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="fb-link"
                              disabled={locked || busy}
                              onClick={() =>
                                run(() => pdApi.settings.addQuestion({ component_id: comp.id }))
                              }
                            >
                              + Add question
                            </button>
                          )}
                        </td>

                        <td style={{ textAlign: 'center' }}>
                          {firstOfComp ? weightDots(comp) : null}
                        </td>
                      </tr>,
                    );

                    if (q && answers.length && openAnswers.has(Number(q.id))) {
                      rows.push(
                        <tr key={`ans-${String(q.id)}`} className="ans-row">
                          {/*
                            TWO empty cells, not one: the answers belong under QUESTION,
                            which is column 3. With a single spacer they started under
                            COMPONENT and read as if they belonged to it (owner, 2026-08-09).
                          */}
                          <td />
                          <td />
                          <td colSpan={2} style={{ padding: '10px 12px' }}>
                            <div className="ans-grid">
                              {answers.map((a) => (
                                <div key={String(a.id)} className="ans-line">
                                  {/* Click cycles 1→5→1, which is how the source lets a
                                      score be changed without leaving the row. */}
                                  <button
                                    type="button"
                                    className="ans-score"
                                    title="Click to change score (1–5)"
                                    disabled={locked || busy}
                                    style={{ backgroundColor: scoreColor(a.score_value) }}
                                    onClick={() =>
                                      run(() =>
                                        pdApi.settings.updateAnswer(Number(a.id), {
                                          score_value: (Number(a.score_value) % 5) + 1,
                                        }),
                                      )
                                    }
                                  >
                                    {String(a.score_value)}
                                  </button>
                                  <input
                                    className="fb-edit"
                                    style={{ fontSize: 11, color: '#475569' }}
                                    defaultValue={String(a.choice_text ?? '')}
                                    disabled={locked}
                                    onBlur={(e) =>
                                      commit(a.choice_text, e.target.value, (v) =>
                                        pdApi.settings.updateAnswer(Number(a.id), {
                                          choice_text: v,
                                        }),
                                      )
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>,
                      );
                    }
                  });
                }

                return <Fragment key={`dim${String(dim.id)}`}>{rows}</Fragment>;
              })}
            </tbody>
          </table>

          {shownTree.length === 0 ? (
            <div className="fb-empty">No dimensions yet — click “Add dimension”.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
